import type { MetaFunction } from "@remix-run/cloudflare";
import { HeroSection } from "~/components/home/HeroSection";
import { ModGrid } from "~/components/mods/ModGrid";
import { ModCard } from "~/components/mods/ModCard";
import { Button } from "~/components/ui/button";
import { Link } from "@remix-run/react";
import { ArrowRight, TrendingUp, Clock, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { useSearchParams } from "@remix-run/react";
import { useUser } from "~/lib/user-context";
import { StructuredData } from "~/components/SEO/StructuredData";
import { Badge } from "~/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { Search, Download, Users, Gamepad2 } from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Stellar Blade Mods - Premium Mod Sharing Community | stellarblademod.com" },
    { 
      name: "description", 
      content: "Discover premium Stellar Blade mods curated by passionate players. Quality-tested modifications for enhanced gameplay, character customization, graphics, and performance optimization." 
    },
    { name: "keywords", content: "stellar blade mods, stellar blade modifications, stellar blade enhancements, gaming mods, character customization, graphics mods, performance optimization, mod community" },
    { name: "author", content: "Stellar Blade Mods Community" },
    { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    { name: "googlebot", content: "index, follow" },
    { name: "bingbot", content: "index, follow" },
    { name: "revisit-after", content: "1 day" },
    { name: "rating", content: "general" },
    { name: "distribution", content: "global" },
    { name: "language", content: "en" },
    { name: "geo.region", content: "US" },
    { name: "geo.placename", content: "United States" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/" },
    
    // Alternative URLs
    { tagName: "link", rel: "alternate", hrefLang: "en", href: "https://stellarblademod.com/" },
    { tagName: "link", rel: "alternate", hrefLang: "x-default", href: "https://stellarblademod.com/" },
    
    // Open Graph
    { property: "og:title", content: "Stellar Blade Mods - Premium Mod Sharing Community" },
    { property: "og:description", content: "Discover premium Stellar Blade mods curated by passionate players. Quality-tested modifications for enhanced gameplay experience." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/" },
    { property: "og:image", content: "https://stellarblademod.com/og-image.jpg" },
    { property: "og:image:alt", content: "Stellar Blade Mods - Premium Gaming Modifications" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:site_name", content: "Stellar Blade Mods" },
    { property: "og:locale", content: "en_US" },
    { property: "og:updated_time", content: "2024-01-15T00:00:00.000Z" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@stellarblademods" },
    { name: "twitter:creator", content: "@stellarblademods" },
    { name: "twitter:title", content: "Stellar Blade Mods - Premium Mod Sharing Community" },
    { name: "twitter:description", content: "Discover premium Stellar Blade mods curated by passionate players. Quality-tested modifications for enhanced gameplay experience." },
    { name: "twitter:image", content: "https://stellarblademod.com/og-image.jpg" },
    { name: "twitter:image:alt", content: "Stellar Blade Mods - Premium Gaming Modifications" },
    
    // Additional SEO
    { name: "theme-color", content: "#0f172a" },
    { name: "msapplication-TileColor", content: "#0f172a" },
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
    { name: "format-detection", content: "telephone=no" },
    { httpEquiv: "content-language", content: "en" },
    { httpEquiv: "X-UA-Compatible", content: "IE=edge" },
    
    // Schema.org JSON-LD will be handled by StructuredData component
  ];
};

// Sample mod data - this will be replaced with real data from the database
const sampleMods = [
  {
    id: "mod-hub",
    title: "Mod Hub - Stellar Blade Mod Manager",
    slug: "mod-hub",
    description: "An application that allows you to easily download, install, and manage mods from the Nexus Mods website",
    version: "1.0.0",
    author_id: "dekita-id",
    category_id: "ui-id",
    mod_type: "UTILITY",
    file_url: null,
    external_download_url: null,
    status: "APPROVED",
    download_count: 2347,
    rating: 4.9,
    review_count: 0,
    created_at: "2025-06-14T00:00:00Z",
    updated_at: "2025-06-14T00:00:00Z",
    author: { 
      id: "dekita-id", 
      username: "DekitaRPG",
      email: "dekita@example.com",
      display_name: "DekitaRPG",
      avatar_url: null,
      bio: null,
      auth_provider: "email",
      role: "USER",
      is_banned: false,
      created_at: "2025-01-01T00:00:00Z",
      updated_at: "2025-01-01T00:00:00Z"
    },
    category: { 
      id: "ui-id", 
      name: "User Interface", 
      slug: "ui", 
      description: "UI mods", 
      icon: null, 
      color: null, 
      created_at: "2025-01-01T00:00:00Z" 
    },
    mod_images: [{ 
      id: "img-1", 
      mod_id: "mod-hub", 
      image_url: "/mods/modhub1.webp", 
      image_type: "PREVIEW", 
      is_primary: true, 
      sort_order: 1, 
      created_at: "2025-06-14T00:00:00Z" 
    }],
    mod_tags: [
      { mod_id: "mod-hub", tag_id: "tag-1", created_at: "2025-06-14T00:00:00Z", tag: { id: "tag-1", name: "mod-manager", slug: "mod-manager", usage_count: 1, created_at: "2025-01-01T00:00:00Z" } },
      { mod_id: "mod-hub", tag_id: "tag-2", created_at: "2025-06-14T00:00:00Z", tag: { id: "tag-2", name: "utility", slug: "utility", usage_count: 1, created_at: "2025-01-01T00:00:00Z" } }
    ]
  }
] as any[];

export default function Index() {
  const [searchParams] = useSearchParams()
  const { refreshUserStatus } = useUser()
  const [scrollPosition, setScrollPosition] = useState(0);

  // 检测用户状态刷新参数
  useEffect(() => {
    const shouldRefresh = searchParams.get('refresh')
    if (shouldRefresh === 'user') {
      // console.log('🔄 首页检测到用户状态刷新请求')
      refreshUserStatus()
      
      // 清除URL参数以避免重复刷新
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('refresh')
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`
      window.history.replaceState({}, '', newUrl)
    }
  }, [searchParams, refreshUserStatus])

  // 检测 OAuth 回调参数，重定向到正确的回调页面
  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    
    if (code) {
      
      // 保持所有参数，重定向到正确的回调页面
      const currentUrl = new URL(window.location.href)
      currentUrl.pathname = '/auth/callback'
      window.location.replace(currentUrl.toString())
    }
  }, [searchParams])
  const cardWidth = 320; // w-80 = 20rem = 320px
  const gap = 24; // gap-6 = 1.5rem = 24px
  const itemWidth = cardWidth + gap;
  
  // Fixed featured mods data - same as mods._index.tsx
  const featuredMods = [
    {
      id: "eve2scarlet",
      title: "EVE2Scarlet",
      slug: "eve2scarlet",
      description: "Transform EVE into Scarlet with complete appearance overhaul including face, hair, body and weapon modifications.",
      version: "1.0.0",
      author_id: "likefengzi-id",
      category_id: "outfits-id",
      mod_type: "CHARACTER",
      download_count: 4523,
      rating: 4.8,
      review_count: 296,
      author: { 
        id: "likefengzi-id", 
        username: "likefengzi",
        display_name: "likefengzi"
      },
      category: { 
        id: "outfits-id", 
        name: "Outfits", 
        slug: "outfits"
      },
      mod_images: [{ 
        id: "img-eve2scarlet", 
        mod_id: "eve2scarlet", 
        url: "/mods/eve2scarlet1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "improved-perfect-defense",
      title: "Improved Perfect Defense",
      slug: "improved-perfect-defense",
      description: "Slightly increased perfect parry/dodge windows and decreased guard cooldown for better responsive gameplay.",
      version: "1.01",
      author_id: "andrew-id",
      category_id: "gameplay-id",
      mod_type: "GAMEPLAY",
      download_count: 8247,
      rating: 4.7,
      review_count: 540,
      author: { 
        id: "andrew-id", 
        username: "andrewRCr",
        display_name: "Andrew Creekmore"
      },
      category: { 
        id: "gameplay-id", 
        name: "Gameplay", 
        slug: "gameplay"
      },
      mod_images: [{ 
        id: "img-ipd", 
        mod_id: "improved-perfect-defense", 
        url: "/mods/ImprovedPerfectDefense1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "minervamon",
      title: "Minervamon",
      slug: "minervamon",
      description: "Transform the Fluffy Bear outfit into Minervamon from Digimon with complete visual overhaul.",
      version: "1.0",
      author_id: "hallow-id",
      category_id: "outfits-id",
      mod_type: "CHARACTER",
      download_count: 1247,
      rating: 4.6,
      review_count: 31,
      author: { 
        id: "hallow-id", 
        username: "HallowSL",
        display_name: "Hallow"
      },
      category: { 
        id: "outfits-id", 
        name: "Outfits", 
        slug: "outfits"
      },
      mod_images: [{ 
        id: "img-minervamon", 
        mod_id: "minervamon", 
        url: "/mods/minervamon1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "ultimate-engine-tweaks",
      title: "Ultimate Engine Tweaks",
      slug: "ultimate-engine-tweaks",
      description: "Definitive Unreal Engine.ini optimizations to remove stutters, improve performance and enhance clarity.",
      version: "1.0",
      author_id: "p40l0-id",
      category_id: "performance-id",
      mod_type: "OPTIMIZATION",
      download_count: 12840,
      rating: 4.9,
      review_count: 763,
      author: { 
        id: "p40l0-id", 
        username: "P40L0",
        display_name: "P40L0"
      },
      category: { 
        id: "performance-id", 
        name: "Performance", 
        slug: "performance"
      },
      mod_images: [{ 
        id: "img-ue-tweaks", 
        mod_id: "ultimate-engine-tweaks", 
        url: "/mods/UltimateEngineTweaks1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    },
    {
      id: "mod-hub",
      title: "Mod Hub - Stellar Blade Mod Manager",
      slug: "mod-hub",
      description: "The ultimate Nexus Mods manager for Stellar Blade. Seamlessly download, install, and manage your mod collection.",
      version: "0.10.10",
      author_id: "dekita-id",
      category_id: "utility-id",
      mod_type: "UTILITY",
      download_count: 2347,
      rating: 4.8,
      review_count: 156,
      author: { 
        id: "dekita-id", 
        username: "Dekita",
        display_name: "DekitaRPG"
      },
      category: { 
        id: "utility-id", 
        name: "Utility", 
        slug: "utility"
      },
      mod_images: [{ 
        id: "img-mod-hub", 
        mod_id: "mod-hub", 
        url: "/mods/modhub1.webp", 
        image_type: "PREVIEW", 
        is_primary: true, 
        sort_order: 1
      }]
    }
  ] as any[];
  
  const totalMods = featuredMods.length;
  const maxScroll = (totalMods - 1) * itemWidth;
  
  const trendingMods = sampleMods.slice(2, 6);
  
  const canGoPrev = scrollPosition > 0;
  const canGoNext = scrollPosition < maxScroll;
  
  const handlePrev = () => {
    if (canGoPrev) {
      setScrollPosition(Math.max(0, scrollPosition - itemWidth));
    }
  };
  
  const handleNext = () => {
    if (canGoNext) {
      setScrollPosition(Math.min(maxScroll, scrollPosition + itemWidth));
    }
  };

  return (
    <div>
      
      {/* Structured Data */}
      <StructuredData type="website" />
      <StructuredData type="organization" />
      
      {/* Hero Section */}
      <HeroSection />

      {/* Editor's Choice Section */}
      <section id="editors-choice" className="container py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Star className="h-8 w-8 text-yellow-500" />
              Editor's Choice
            </h2>
            <p className="text-muted-foreground mt-2">
              Carefully selected mods by our editors
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Navigation arrows */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                disabled={!canGoPrev}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={!canGoNext}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" asChild>
              <Link to="/mods">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        
        {/* Horizontal scrolling mod cards */}
        <div className="overflow-hidden">
          <div 
            className="flex gap-6 transition-transform duration-300 ease-in-out"
            style={{ 
              transform: `translateX(-${scrollPosition}px)`,
              width: 'max-content'
            }}
          >
            {featuredMods.map((mod) => (
              <div key={mod.id} className="flex-shrink-0 w-80">
                <ModCard mod={mod} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Progress Section */}
      <section className="bg-slate-950 py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Left Module: What We're Building */}
            <div className="space-y-6">
              <div className="border-l-4 border-cyan-400 pl-6">
                <h2 className="text-2xl font-bold text-slate-100 mb-4 font-mono">
                  &gt; WHAT_WE_ARE_BUILDING.exe
                </h2>
                <div className="bg-slate-900 text-green-400 p-6 rounded-lg font-mono text-sm border border-slate-700">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-cyan-400">SYSTEM_STATUS:</span> 
                    <span className="text-yellow-400">BUILDING</span>
                  </div>
                  
                  <div className="space-y-3 text-green-300">
                    <p className="typing-effect">We are building a premium mod sharing community.</p>
                    <p className="typing-effect">Currently, we are carefully curating and testing various high-quality mods.</p>
                    <p className="typing-effect">As a devoted Stellar Blade player, I deeply understand the struggle of finding truly exceptional mods.</p>
                    <p className="typing-effect">This website was born from a simple wish: to share amazing discoveries with fellow enthusiasts.</p>
                    <p className="typing-effect">Every recommendation here comes from genuine player experience, not automated algorithms or paid promotions.</p>
                    <p className="typing-effect">My hope is that every mod featured on this site has been personally tested and loved by real players.</p>
                    <p className="text-cyan-400">Please stay tuned for our official launch!</p>
                  </div>
                  
                  <div className="mt-6 border-t border-slate-700 pt-4">
                    <div className="flex items-center gap-2 text-yellow-400">
                      <span className="animate-pulse">●</span>
                      <span>INITIALIZING_COMMUNITY.protocol</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Module: Development Log */}
            <div className="space-y-6">
              <div className="border-l-4 border-pink-400 pl-6">
                <h2 className="text-2xl font-bold text-slate-100 mb-4 font-mono">
                  &gt; DEV_LOG.json
                </h2>
                <div className="bg-slate-900 text-white p-6 rounded-lg font-mono text-sm border border-slate-700">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
                    <span className="text-pink-400">BUILD_VERSION:</span> 
                    <span className="text-green-400">0.0.1-alpha</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">2025.06.16 - Official Website Launch</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">2025.06.17 - Add 3 Featured Mods</span>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">2025.06.18 - Discord Community</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">2025.06.19 - Detailed Installation Guides</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-green-400 mt-1">✓</span>
                      <span className="text-gray-300">2025.06.22 - User Registration System</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-red-400 mt-1">✓</span>
                      <span className="text-red-300">2025.06.27 - User Upload System</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <span className="text-orange-400 mt-1">🔧</span>
                      <span className="text-orange-300">Coming Soon - Community Interaction Features</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 border-t border-slate-700 pt-4">
                    <div className="flex items-center gap-2 text-cyan-400">
                      <span className="animate-ping inline-block w-1 h-1 bg-cyan-400 rounded-full"></span>
                      <span>COMPILING_NEXT_FEATURES...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buy Stellar Blade PC Section */}
      <section 
        className="py-16 bg-cover bg-center bg-no-repeat relative flex items-center"
        style={{
          backgroundImage: 'url(/mods/stellar-blade-nier-automata.webp)'
        }}
      >
        {/* Background overlay */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Section Header */}
            <div className="mb-12">
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Experience the Ultimate
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                  Action Adventure
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Get Stellar Blade PC version with exclusive discounts on Steam
              </p>
            </div>
            
            {/* Purchase Card */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 md:p-12 max-w-2xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-semibold text-lg">SPECIAL OFFER</span>
                </div>
                <h3 className="text-3xl font-bold text-white mb-2">Stellar Blade PC</h3>
                <p className="text-gray-300">Digital Download • Instant Access</p>
              </div>
              
              {/* Purchase Button */}
              <Link 
                to="/Stellar-Blade-PC"
                className="block w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white font-bold text-lg py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg text-center"
              >
                Buy Stellar Blade PC at Discount Price
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Mods Section */}
      {/* <section className="bg-muted/50 py-16">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold flex items-center gap-2">
                <TrendingUp className="h-8 w-8 text-green-500" />
                Trending Now
              </h2>
              <p className="text-muted-foreground mt-2">
                Popular mods this week
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/mods/trending">
                View Trending <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <ModGrid mods={trendingMods} />
        </div>
      </section> */}

      {/* Recent Mods Section */}
      {/* <section className="container py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold flex items-center gap-2">
              <Clock className="h-8 w-8 text-blue-500" />
              Latest Releases
            </h2>
            <p className="text-muted-foreground mt-2">
              Fresh mods from our creators
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/mods/new">
              View Recent <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <ModGrid mods={sampleMods.slice(0, 4)} />
      </section> */}

      {/* Call to Action Section */}
      {/* <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Modding?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of creators and players in the ultimate Stellar Blade modding community
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" asChild>
              <Link to="/register">
                Get Started
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" asChild>
              <Link to="/upload">
                Upload Your Mod
              </Link>
            </Button>
          </div>
        </div>
      </section> */}

    </div>
  );
}
